/**
 * Week Calendar View Component
 * Outlook-style weekly calendar with time slots
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  eachHourOfInterval,
  startOfDay,
  endOfDay,
  isSameDay,
  isToday,
  format,
  parseISO,
  getHours,
  getMinutes,
  differenceInMinutes,
} from 'date-fns'
import { CalendarEventDto } from '@api/types/calendarEvent'

interface WeekCalendarViewProps {
  currentDate: Date
  events: CalendarEventDto[]
  onDateClick: (date: Date) => void
}

const HOUR_HEIGHT = 60 // pixels per hour
const START_HOUR = 6 // 6 AM
const END_HOUR = 22 // 10 PM

export function WeekCalendarView({
  currentDate,
  events,
  onDateClick,
}: WeekCalendarViewProps) {
  const theme = useTheme()
  const navigate = useNavigate()

  // Get days of the week
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 })
    const end = endOfWeek(currentDate, { weekStartsOn: 0 })
    return eachDayOfInterval({ start, end })
  }, [currentDate])

  // Generate hour slots
  const hours = useMemo(() => {
    const slots: number[] = []
    for (let h = START_HOUR; h <= END_HOUR; h++) {
      slots.push(h)
    }
    return slots
  }, [])

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEventDto[]> = {}
    events.forEach((event) => {
      const dateKey = format(parseISO(event.startDateTime), 'yyyy-MM-dd')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(event)
    })
    return grouped
  }, [events])

  const getEventsForDate = (date: Date): CalendarEventDto[] => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return eventsByDate[dateKey] || []
  }

  const getEventPosition = (event: CalendarEventDto) => {
    const startTime = parseISO(event.startDateTime)
    const endTime = parseISO(event.endDateTime)

    const startHour = getHours(startTime)
    const startMinute = getMinutes(startTime)

    // Calculate top position relative to START_HOUR
    const topMinutes = (startHour - START_HOUR) * 60 + startMinute
    const top = Math.max(0, topMinutes * (HOUR_HEIGHT / 60))

    // Calculate height based on duration
    const durationMinutes = differenceInMinutes(endTime, startTime)
    const height = Math.max(20, durationMinutes * (HOUR_HEIGHT / 60))

    return { top, height }
  }

  const handleEventClick = (e: React.MouseEvent, eventId: string) => {
    e.stopPropagation()
    navigate(`/events/${eventId}`)
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Header with day names */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '60px repeat(7, 1fr)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'grey.100',
        }}
      >
        {/* Empty corner */}
        <Box sx={{ borderRight: `1px solid ${theme.palette.divider}` }} />

        {/* Day headers */}
        {weekDays.map((day) => {
          const isCurrentDay = isToday(day)
          return (
            <Box
              key={day.toISOString()}
              sx={{
                py: 1,
                textAlign: 'center',
                minWidth: 0,
                borderRight: `1px solid ${theme.palette.divider}`,
                '&:last-child': { borderRight: 'none' },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {format(day, 'EEE')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  width: 36,
                  height: 36,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: isCurrentDay ? 'primary.main' : 'transparent',
                  color: isCurrentDay ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {format(day, 'd')}
              </Typography>
            </Box>
          )
        })}
      </Box>

      {/* Time grid */}
      <Box sx={{ display: 'flex', maxHeight: 600, overflow: 'auto' }}>
        {/* Time labels */}
        <Box sx={{ width: 60, flexShrink: 0 }}>
          {hours.map((hour) => (
            <Box
              key={hour}
              sx={{
                height: HOUR_HEIGHT,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                pr: 1,
                pt: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {format(new Date().setHours(hour, 0), 'h a')}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day columns */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flex: 1 }}>
          {weekDays.map((day) => {
            const dayEvents = getEventsForDate(day)
            const isCurrentDay = isToday(day)

            return (
              <Box
                key={day.toISOString()}
                sx={{
                  position: 'relative',
                  minWidth: 0,
                  overflow: 'hidden',
                  borderRight: `1px solid ${theme.palette.divider}`,
                  '&:last-child': { borderRight: 'none' },
                  bgcolor: isCurrentDay ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
                }}
                onClick={() => onDateClick(day)}
              >
                {/* Hour grid lines */}
                {hours.map((hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: HOUR_HEIGHT,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  />
                ))}

                {/* Events */}
                {dayEvents.map((event) => {
                  const { top, height } = getEventPosition(event)
                  const startTime = parseISO(event.startDateTime)

                  return (
                    <Box
                      key={event.id}
                      onClick={(e) => handleEventClick(e, event.id)}
                      sx={{
                        position: 'absolute',
                        top,
                        left: 2,
                        right: 2,
                        height,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: 0.5,
                        p: 0.5,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          zIndex: 1,
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {event.title}
                      </Typography>
                      {height > 30 && (
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.9,
                            fontSize: '0.65rem',
                          }}
                        >
                          {format(startTime, 'h:mm a')}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Paper>
  )
}
